import apiClient from '../utils/api';
import type { VendorLocale } from './vendorProfileService';

export interface VendorSession {
  vendorId: string;
  email: string;
  contactName: string;
  companyName: string;
  companyDescription?: string;
  companyWebsite?: string;
  locale?: VendorLocale;
  phone: string;
  businessType: string;
  onboardingStep: number;
  needsPasswordSetup: boolean;
}

export interface TokenValidationResponse {
  success: boolean;
  message: string;
  vendor: VendorSession;
  session: {
    vendorId: string;
    email: string;
    isAuthenticated: boolean;
    tokenType: string;
  };
}

export interface TokenValidationError {
  success: false;
  error: string;
}

export interface SetPasswordData {
  vendorId: string;
  password: string;
}

export interface SetPasswordResponse {
  success: boolean;
  message: string;
  vendor?: VendorSession;
  accessToken?: string;
  refreshToken?: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  vendor: VendorSession;
  accessToken: string;
  refreshToken: string;
}

export interface ForgotPasswordData {
  email: string;
}

export interface ForgotPasswordResponse {
  success: boolean;
  message: string;
}

export interface ResetPasswordData {
  token: string;
  newPassword: string;
}

export interface ResetPasswordResponse {
  success: boolean;
  message: string;
}

export interface ValidateResetTokenData {
  token: string;
}

export interface ValidateResetTokenResponse {
  success: boolean;
  message: string;
  vendor?: {
    vendorId: string;
    email: string;
    contactName: string;
    companyName?: string;
    phone: string;
    businessType: string;
    onboardingStep: number;
    needsPasswordSetup?: boolean;
  };
}

export const vendorAuthService = {
  /**
   * Validate vendor onboarding token
   */
  validateToken: async (token: string): Promise<TokenValidationResponse> => {
    const response = await apiClient.post<TokenValidationResponse>(
      '/auth/vendors/verify-token',
      { token }
    );
    return response.data;
  },

  /**
   * Set vendor password
   */
  setPassword: async (vendorId: string, password: string, onboardingToken?: string): Promise<SetPasswordResponse> => {
    // Create a custom config for this request to include the onboarding token
    const config = {
      headers: {
        ...(onboardingToken && { Authorization: `Bearer ${onboardingToken}` })
      }
    };
    
    const response = await apiClient.post<SetPasswordResponse>(
      `/vendors/${vendorId}/set-password`,
      { password },
      config
    );
    return response.data;
  },

  /**
   * Login vendor with email and password
   */
  login: async (email: string, password: string): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>(
      '/auth/vendors/login',
      { email, password }
    );
    return response.data;
  },

  /**
   * Refresh vendor access token
   */
  refreshToken: async (refreshToken: string): Promise<{ accessToken: string; refreshToken: string }> => {
    const response = await apiClient.post<{ accessToken: string; refreshToken: string }>(
      '/auth/vendors/refresh-token',
      { refreshToken }
    );
    return response.data;
  },

  /**
   * Request password reset email
   */
  requestPasswordReset: async (email: string): Promise<ForgotPasswordResponse> => {
    const response = await apiClient.post<ForgotPasswordResponse>(
      '/auth/vendors/forgot-password',
      { email }
    );
    return response.data;
  },

  /**
   * Reset password using token
   */
    resetPassword: async (token: string, newPassword: string): Promise<ResetPasswordResponse> => {
      const response = await apiClient.post<ResetPasswordResponse>(
        '/auth/vendors/reset-password',
        { token, newPassword }
      );
      return response.data;
    },

    /**
     * Validate password reset token and get vendor email
     */
    validateResetToken: async (token: string): Promise<ValidateResetTokenResponse> => {
      const response = await apiClient.post<ValidateResetTokenResponse>(
        '/auth/vendors/validate-reset-token',
        { token }
      );
      return response.data;
    },
};
